import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons/faArrowRight';
import React, { useCallback } from 'react';

import { get } from 'lodash-es';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';

import { Button } from 'ui-components/components/button';

import MainContent from './main-content';
import SellerProviderList from './seller-provider-list';
import { useFilteredLocalDeliveryProviders } from '../../hooks/use-filtered-local-delivery-providers';
import { useFilteredSellerLabelProviders } from '../../hooks/use-filtered-seller-label-providers';
import { useServicesByProviderId } from '../../hooks/use-services-by-provider-id';
import Global from '../../store/global-selectors';
import { selectRate, selectSellerProvider } from '../../store/rate-viewer/rate-viewer.actions';
import { UpgradePlanPromotionalBanner } from '../upgrade-plan-promotional-banner/upgrade-plan-promotional-banner';

import style from './rate-viewer.module.less';
import { WorkflowSteps } from '../../tracking/use-workflow-tracking';
import { useTrackRateBrowser } from './use-track-rate-browser';
import { messages } from './messages';

const rateBrowserFulfillmentPlanId = 'rate-browser';

const getSelectedSellerProviderId = (rateViewerSellerProviderId, sellerProviderRates) => {
  return !rateViewerSellerProviderId && sellerProviderRates.length > 0
    ? sellerProviderRates[0].sellerLabelProviderId
    : rateViewerSellerProviderId;
};

export const OldRateViewer = ({ hideLocalDelivery = false, rateViewerKey, buttonTheme = '', onConfigureLabel }) => {
  const dispatch = useDispatch();
  const filteredBYOLProviders = useFilteredSellerLabelProviders();
  const filteredLocalDeliveryProviders = useFilteredLocalDeliveryProviders(filteredBYOLProviders);
  const sellerLabelProviders = hideLocalDelivery ? filteredLocalDeliveryProviders : filteredBYOLProviders;
  const isLoading = useSelector((state) => Global.selectRateViewerIsLoading(state, { rateViewerKey }));
  const sellerProviderRates = useSelector((state) => Global.selectRateViewerRates(state, { rateViewerKey }));
  const selectedRate = useSelector((state) => Global.selectRateViewerRate(state, { rateViewerKey }));
  const validationErrors = useSelector((state) => Global.selectRateViewerErrors(state, { rateViewerKey }));
  const rateViewerSellerProviderId = useSelector((state) => Global.selectRateViewerSellerProviderId(state, { rateViewerKey }));
  const selectedSellerProviderId = getSelectedSellerProviderId(rateViewerSellerProviderId, sellerProviderRates);
  const trackRateBrowser = useTrackRateBrowser();

  const configureLabel = useCallback(() => {
    trackRateBrowser(WorkflowSteps.RATE_BROWSER_CREATE_LABEL_CLICKED);
    onConfigureLabel({
      sellerLabelProviderId: selectedSellerProviderId,
      rate: selectedRate,
      fulfillmentPlanId: rateBrowserFulfillmentPlanId,
    });
  }, [onConfigureLabel, selectedRate, selectedSellerProviderId, trackRateBrowser]);

  const onRateSelected = useCallback(
    (rate) => {
      trackRateBrowser(WorkflowSteps.RATE_BROWSER_PROVIDER_RATE_CLICKED, {
        rate,
      });
      dispatch(selectRate(rateViewerKey, rate));
    },
    [dispatch, rateViewerKey, trackRateBrowser],
  );

  const onSellerProviderClicked = useCallback(
    (sellerProviderId) => {
      trackRateBrowser(WorkflowSteps.RATE_BROWSER_PROVIDER_CLICKED, {
        providerId: sellerProviderId,
      });
      dispatch(selectSellerProvider(rateViewerKey, sellerProviderId));
    },
    [dispatch, rateViewerKey, trackRateBrowser],
  );

  const selectedProvider = sellerProviderRates.find(
    (sellerProviderRate) => sellerProviderRate.sellerLabelProviderId === selectedSellerProviderId,
  );
  const servicesByProvider = useServicesByProviderId(get(selectedProvider, 'labelProviderId'));
  const hiddenServices = servicesByProvider.filter((service) => service.isHidden).map((service) => service.serviceId);
  if (selectedProvider) {
    selectedProvider.rates = selectedProvider.rates.filter((rate) => !hiddenServices.includes(rate.serviceId));
  }

  return (
    <div className={style['rate-viewer']}>
      <UpgradePlanPromotionalBanner
        className={style['upgrade-plan-promotional-banner']}
        promotionalHeaderMessage={messages.upgradePromotionalBannerTitle}
      />
      <div className={style['rate-viewer-body']}>
        <div className={style['normal-content']}>
          <div className={style['seller-provider-list']}>
            <SellerProviderList
              onSellerProviderClicked={onSellerProviderClicked}
              selectedSellerProviderId={selectedSellerProviderId}
              sellerLabelProviders={sellerLabelProviders}
              sellerProviderRates={sellerProviderRates}
            />
          </div>
          <MainContent
            isLoading={isLoading}
            onRateSelected={onRateSelected}
            selectedProvider={selectedProvider}
            selectedRate={selectedRate}
            sellerProviderRates={sellerProviderRates}
            validationErrors={validationErrors}
          />
        </div>
      </div>
      <div className={style['rate-viewer-footer']}>
        <Button
          className={style['configure-label-button']}
          disabled={!selectedRate}
          onClick={configureLabel}
          testId='configure-label-button'
          theme={buttonTheme}
        >
          <div className={style['configure-label-button-text']}>
            <FormattedMessage defaultMessage='Configure Label' description='rateViewer.configureLabel' id='V7vApW' />
          </div>
          <FontAwesomeIcon className={style['configure-label-icon']} icon={faArrowRight} />
        </Button>
      </div>
    </div>
  );
};

OldRateViewer.propTypes = {
  hideLocalDelivery: PropTypes.bool,
  rateViewerKey: PropTypes.string.isRequired,
  buttonTheme: PropTypes.oneOf(['', 'primary']),
  onConfigureLabel: PropTypes.func,
};
